/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import { useCopyToClipboard } from '@uidotdev/usehooks';
import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { useCreateCustomSnapshot, useCreateSnapshot } from '~/apis';
import { useFirebaseAnalytics } from '~/apis/useFirebase';
import {
  DEBUG_enabled,
  gameLogger,
  selectGameId,
  selectGameType,
  selectRound,
  selectTurnType,
  useTranslation,
} from '~/app';
import { config } from '~/app/config';
import { loadLocalGameSnapshot } from '~/app/controllerLocalWrapper';
import { ReactComponent as EmailIcon } from '~/components/AccountMenu/email.svg';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '~/components/Dialog/Dialog';
import { Button, Switch, TextField } from '~/components/Form';
import { GameState, GameType } from '~/server/src/dtos';
import { captureUiContext, useOnStateChange } from '~/utils';

import { ReactComponent as CopyIcon } from '../button-icons/button-copy.svg';
import { ReactComponent as ImgWhatsApp } from '../button-icons/whatsapp.svg';

interface ReportProps {
  isOpen: boolean;
  onClose: () => void;
  /** Element that opened the report (e.g. menu item) for UI context capture. */
  triggerElement?: Element | null;
}

const Report = ({ isOpen, onClose, triggerElement = null }: ReportProps): JSX.Element => {
  const analytics = useFirebaseAnalytics();

  const gameId = useSelector(selectGameId);
  const gameType = useSelector(selectGameType);
  const turnType = useSelector(selectTurnType);
  const round = useSelector(selectRound);

  const { t } = useTranslation();

  const [, copyToClipboard] = useCopyToClipboard();
  const [message, setMessage] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [customGameState, setCustomGameState] = useState('');
  const [isCustomGameState, setIsCustomGameState] = useState(false);
  const [copyGameIdSuccess, setCopyGameIdSuccess] = useState(false);

  const uiContext = useMemo(
    () => (isOpen ? captureUiContext(triggerElement ?? null) : null),
    [isOpen, triggerElement],
  );

  const handleCopyGameId = useCallback(() => {
    if (gameId) {
      copyToClipboard(gameId);
      setCopyGameIdSuccess(true);
      setTimeout(() => setCopyGameIdSuccess(false), 2000);
    }
  }, [gameId, copyToClipboard]);

  const createCustomSnapshot = useCreateCustomSnapshot({
    onSuccess: () => {
      onClose();
      analytics?.logEvent('send_report', { round, turnType, gameType, isCustom: true });
    },
  });
  const createSnapshot = useCreateSnapshot(gameId, {
    onSuccess: () => {
      onClose();
      analytics?.logEvent('send_report', { round, turnType, gameType, isCustom: false });
    },
  });

  const payload = {
    message,
    userAgent: navigator.userAgent,
    contactEmail: contactEmail.trim() || undefined,
    uiContext: uiContext ?? undefined,
  };

  const sendReport = () => {
    // send custom snapshot pasted into ui textfield
    if (isCustomGameState && customGameState && customGameState.length > 0) {
      try {
        const parsedGameState = JSON.parse(customGameState) as GameState;
        createCustomSnapshot.mutate({
          ...payload,
          gameState: parsedGameState,
        });
      } catch (e) {
        gameLogger.warn({ err: e }, 'error parsing custom game state');
      }
    }

    // create snapshot on server if current game is a remote game
    else if (gameType !== GameType.Training && gameId !== undefined) {
      // TODO: make sure gameType is set after restore and remove gameId conditional
      createSnapshot.mutate(payload);
    }

    // create snapshot from local storage in training mode
    else if (gameType === GameType.Training || gameId === undefined) {
      // TODO: make sure gameType is set after restore and remove gameId conditional
      const parsedGameState = loadLocalGameSnapshot();
      if (parsedGameState)
        createCustomSnapshot.mutate({
          ...payload,
          gameState: parsedGameState,
        });
    }
  };

  const isLoading = createSnapshot.isPending || createCustomSnapshot.isPending;

  const cancel = useCallback(() => {
    onClose();
    analytics?.logEvent('cancel_report', {
      round,
      turnType,
      gameType,
      isCustom: gameType === GameType.Training,
    });
  }, [analytics, gameType, onClose, round, turnType]);

  useOnStateChange(
    isOpen,
    useCallback(
      (newIsOpen) => {
        if (newIsOpen) {
          analytics?.logEvent('begin_report', {
            round,
            turnType,
            gameType,
            isCustom: gameType === GameType.Training,
          });
        }
      },
      [analytics, gameType, round, turnType],
    ),
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && cancel()}>
      <DialogContent
        size="2xl"
        className="w-full max-w-[95vw] sm:max-w-xl md:max-w-2xl lg:max-w-3xl"
      >
        <DialogTitle className="flex items-center space-x-2">
          <span>{t('report_title')}</span>
          {isLoading && (
            <div className="h-4 w-4 animate-spin rounded-full border-b-2 border-primary"></div>
          )}
        </DialogTitle>
        <DialogDescription className="selectable-text">{t('report_content')}</DialogDescription>

        <p className="selectable-text mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-600">
          <span>{t('report_alternatives')}</span>
          {config.whatsappEnabled && config.whatsappInviteUrl && (
            <>
              <a
                href={config.whatsappInviteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-primary underline hover:text-primary-light"
              >
                <ImgWhatsApp className="h-4 w-4 shrink-0" aria-hidden />
                {t('report_alternatives_whatsapp')}
              </a>
              <span aria-hidden>–</span>
            </>
          )}
          <a
            href="mailto:support@turfmaster.de"
            className="inline-flex items-center gap-1.5 text-primary underline hover:text-primary-light"
          >
            <EmailIcon className="h-4 w-4 shrink-0" aria-hidden />
            {t('report_alternatives_email')}
          </a>
        </p>

        <div className="mt-4 flex flex-col space-y-4">
          <div className="flex flex-col space-y-2">
            <label htmlFor="report-description" className="text-sm font-medium text-gray-700">
              {t('report_description')}
            </label>
            <textarea
              id="report-description"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t('report_description_placeholder')}
              rows={4}
              className="custom-input-focus min-h-[100px] w-full resize-y rounded-md border border-gray-300 bg-white px-3 py-2 text-left text-sm placeholder:text-gray-500 focus:ring-2 focus:ring-primary focus:ring-offset-0 focus:outline-hidden disabled:cursor-not-allowed disabled:opacity-50"
            />
          </div>

          <TextField
            label={t('report_contact_label')}
            value={contactEmail}
            onChange={(e) => setContactEmail(e.target.value)}
            placeholder={t('report_contact_placeholder')}
          />

          {gameId !== undefined && (
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-gray-700">{t('report_gameId_label')}</h3>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={gameId}
                  readOnly
                  className="flex-1 rounded-md border border-gray-300 bg-gray-50 px-3 py-2 font-mono text-sm"
                  aria-label={t('report_gameId_label')}
                />
                <Button
                  variant={copyGameIdSuccess ? 'secondary' : 'primary'}
                  size="sm"
                  onClick={handleCopyGameId}
                  className="flex items-center space-x-1"
                >
                  <CopyIcon className="h-4 w-4" aria-hidden />
                  <span>{copyGameIdSuccess ? t('invitation_copied') : t('invitation_copy')}</span>
                </Button>
              </div>
            </div>
          )}

          {/* Dev/debug only: switch and custom game state field – hidden in production to avoid confusing users */}
          {DEBUG_enabled && (
            <>
              <div className="flex items-center space-x-3">
                <Switch
                  checked={isCustomGameState}
                  onCheckedChange={setIsCustomGameState}
                  id="custom-game-state-switch"
                />
                <label
                  htmlFor="custom-game-state-switch"
                  className="cursor-pointer text-sm font-medium text-gray-700"
                >
                  {isCustomGameState
                    ? t('report_gameStateSwitch_on')
                    : t('report_gameStateSwitch_off')}
                </label>
              </div>
              {isCustomGameState && (
                <TextField
                  label={t('report_customGameState')}
                  value={customGameState}
                  onChange={(e) => setCustomGameState(e.currentTarget.value)}
                  className="min-h-[150px]"
                  inputClassName="min-h-[150px] resize-y font-mono text-xs"
                />
              )}
            </>
          )}
        </div>

        <div className="mt-6 flex justify-end space-x-3">
          <Button variant="secondary" onClick={cancel}>
            {t('report_cancel')}
          </Button>
          <Button variant="primary" onClick={sendReport} disabled={message.length === 0}>
            {t('report_send')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default Report;
